<template>
    <div class="grid grid-cols-1 gap-3 md:grid-cols-2 md:gap-5">
        <BaseCard as="section" class="flex flex-col gap-1" :aria-label="t('components.profileWizard.comparison.sampleProfileLabel', { name: SAMPLE_SENDER_NAME })">
            <div class="flex items-center gap-2 border-b border-border pb-2">
                <CollaborationProfileAvatar size="sm" :name="SAMPLE_SENDER_NAME" />
                <div class="min-w-0">
                    <div class="flex items-center gap-1">
                        <h3 class="nadia-chat-bold text-ink">{{ SAMPLE_SENDER_NAME }}</h3>
                        <BaseChip size="2xs">{{ t("components.profileWizard.comparison.sample") }}</BaseChip>
                    </div>
                    <p class="scaffold-details-small text-ink-muted">{{ t("components.profileWizard.comparison.sharedWithYou") }}</p>
                </div>
            </div>
            <ul class="flex flex-col gap-1 px-2.5">
                <li v-for="trait in traits" :key="trait.text" class="flex h-6 items-center gap-2.5">
                    <component :is="trait.icon.component" :style="{ color: trait.icon.color }" class="size-3.5 shrink-0" aria-hidden="true" />
                    <span class="scaffold-details-small text-ink">{{ trait.text }}</span>
                </li>
            </ul>
        </BaseCard>
        <BaseCard as="section" class="flex flex-col gap-1" :aria-label="t('components.profileWizard.comparison.viewerLabel', { name: viewerName })">
            <div class="flex items-center gap-2 border-b border-border pb-2">
                <CollaborationProfileAvatar size="sm" :name="viewerName" :available="false" />
                <div class="min-w-0">
                    <h3 class="nadia-chat-bold text-ink">{{ viewerName }}</h3>
                    <p class="scaffold-details-small text-ink-muted">{{ t("components.profileWizard.comparison.bestGuess") }}</p>
                </div>
            </div>
            <div class="flex flex-1 flex-col justify-between gap-4 pt-2" aria-hidden="true">
                <div v-for="width in placeholderWidths" :key="width" class="flex items-center gap-2.5">
                    <span class="size-3 shrink-0 rounded-full border border-dashed border-border-strong" />
                    <span class="h-1.75 rounded-full bg-border" :style="{ width }" />
                </div>
            </div>
        </BaseCard>
    </div>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import BaseChip from "~vue/components/BaseChip.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import { COLLAB_ICONS } from "~vue/utils/collabIcons";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import { SAMPLE_SENDER_NAME } from "./profileLandingConstants";

const { t } = useI18n();

defineProps<{ viewerName: string }>();

const traits = computed(() => [
    { icon: COLLAB_ICONS.communicationStyle, text: t("components.profileWizard.comparison.traits.communication") },
    { icon: COLLAB_ICONS.workStructure, text: t("components.profileWizard.comparison.traits.structure") },
    { icon: COLLAB_ICONS.decisionMaking, text: t("components.profileWizard.comparison.traits.decisions") },
    { icon: COLLAB_ICONS.informationProcessing, text: t("components.profileWizard.comparison.traits.bigPicture") },
    { icon: COLLAB_ICONS.feedbackCollaboration, text: t("components.profileWizard.comparison.traits.feedback") },
    { icon: COLLAB_ICONS.motivatorsStressors, text: t("components.profileWizard.comparison.traits.motivation") },
]);
// Bar lengths from the Figma skeleton, as a share of the row width.
const placeholderWidths = ["84%", "54%", "67%", "84%", "65%", "86%"];
</script>
